/**
 * Parent-facing checkout error messages.
 *
 * `supabase.functions.invoke` throws a FunctionsHttpError for any non-2xx and
 * its `message` is the useless "Edge Function returned a non-2xx status code".
 * The real payload lives on `error.context` (a Response). This helper reads it
 * and maps the server's stable error code to something a parent can act on.
 */

export const CHECKOUT_ERROR_MESSAGES: Record<string, string> = {
  duplicate_child_registration: "This child is already registered. If you believe this is an error, please contact Silver Sage.",
  registration_in_progress: "Registration has already been started. Return to your original checkout to continue, or try again after it expires.",
  validation_failed: "Please review the highlighted registration fields and try again.",
  class_not_found:
    "We could not find this class. Please refresh the page, or email silversagehomeschoolhubs@gmail.com and we'll register you.",
  class_closed:
    "This class is not currently accepting registrations. Email silversagehomeschoolhubs@gmail.com and we'll help.",
  externally_managed:
    "This class is registered through its provider — please use the provider's sign-up link on the class page.",
  session_unavailable:
    "This session is no longer available. Please choose another session, or email silversagehomeschoolhubs@gmail.com.",
  capacity_full:
    "This session has just filled. Please choose another session or join the interest list.",
  duplicate_submission:
    "This registration may already have been submitted. Please check your email before trying again.",
  registration_blocked:
    "Registration is not available for this account. Please contact silversagehomeschoolhubs@gmail.com.",
  price_unavailable:
    "Pricing for this class is not published yet. Email silversagehomeschoolhubs@gmail.com and we'll register you.",
  stripe_unavailable:
    "We could not open the secure payment page. Your card has not been charged — please try again in a moment.",
  server_error:
    "We could not complete your registration right now. Your card has not been charged. Please try again, or email silversagehomeschoolhubs@gmail.com.",
};

const FALLBACK =
  "We could not complete your registration right now. Your card has not been charged. Please try again, or email silversagehomeschoolhubs@gmail.com.";

const JARGON = [
  "non-2xx",
  "FunctionsHttpError",
  "Failed to fetch",
  "NetworkError",
  "Load failed",
];

function looksLikeJargon(msg: string): boolean {
  return JARGON.some((j) => msg.toLowerCase().includes(j.toLowerCase()));
}

/** Resolve any checkout failure into a message safe to show a parent. */
export async function resolveCheckoutError(err: unknown): Promise<string> {
  const ctx = (err as { context?: unknown })?.context;
  if (ctx && typeof (ctx as Response).text === "function") {
    try {
      const raw = await (ctx as Response).clone().text();
      const body = JSON.parse(raw) as { code?: string; error?: string };
      if (body.code && CHECKOUT_ERROR_MESSAGES[body.code]) {
        // Server-provided message wins when it's specific (e.g. seats left).
        return body.error || CHECKOUT_ERROR_MESSAGES[body.code];
      }
      if (body.error && !looksLikeJargon(body.error)) return body.error;
    } catch {
      // fall through to the generic message
    }
  }

  const msg = err instanceof Error ? err.message : String(err ?? "");
  if (!msg || looksLikeJargon(msg)) return FALLBACK;
  return msg;
}
